import { ReactNode, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import NextLink from 'next/link';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  createIcon,
  Flex,
  Grid,
  LinkBox,
  LinkOverlay,
  Stack,
  StyleProps,
  Text,
} from '@chakra-ui/react';
import { CheckedFilledIcon, GraduationIcon, HomeIcon, SettingsIcon, StarFullIcon } from '@udacity/chakra-uds-icons';

import { Link } from '~/components/link';
import { getUserType } from '~/features/user/controllers/get-user-type';
import { useBaseUrl } from '~/hooks/use-base-url';

import { FooterSectionIcon, NavGroup, NavLink } from '../types';
import { flattenNavGroups, trackFooterLinkClick } from '../utils';

// Original footer "Company" building glyph (native viewBox 0 0 24 24). Scaled and
// centered into a 0 0 32 32 box so it renders at the same optical size as the UDS
// icons, which fill roughly the center 22 units of their 32 viewBox.
const CompanyIcon = createIcon({
  displayName: 'FooterCompanyIcon',
  viewBox: '0 0 32 32',
  path: (
    <g transform='translate(2.556 1.943) scale(1.222)'>
      <path d='M4 20V5h12v15M8 9h2m2 0h2M8 13h2m2 0h2M2 20h18' fill='none' stroke='currentColor' strokeWidth='1.5' />
    </g>
  ),
});

// Original Figma school-building glyph (native viewBox 0 0 15.0363 14.2508). It is
// scaled and centered into a 0 0 32 32 box so it renders at the same optical size
// as the UDS icons, which fill roughly the center 22 units of their 32 viewBox.
const SchoolsIcon = createIcon({
  displayName: 'FooterSchoolsIcon',
  viewBox: '0 0 32 32',
  path: (
    <g transform='translate(5 5.6) scale(1.463)'>
      <path
        d='M7.49491 0.000732437C7.40084 0.00561527 7.30969 0.03361 7.22961 0.0827637L0.292114 4.32365C0.0310465 4.46655 -0.0731201 4.78817 0.0541586 5.05737C0.181437 5.32625 0.496541 5.44963 0.772583 5.33862V13.1257H0.585083C0.382284 13.1228 0.193481 13.2296 0.0912679 13.4047C-0.0109457 13.5798 -0.0109457 13.7966 0.0912679 13.9718C0.193481 14.1469 0.382284 14.2537 0.585083 14.2507H14.4601C14.6629 14.2537 14.8517 14.1469 14.9539 13.9718C15.0561 13.7966 15.0561 13.5798 14.9539 13.4047C14.8517 13.2296 14.6629 13.1228 14.4601 13.1257H14.2726V5.33732C14.547 5.44214 14.8562 5.31844 14.9819 5.05282C15.1079 4.78719 15.0083 4.46948 14.7531 4.32365L7.81555 0.0827637C7.73547 0.03361 7.64433 0.00561527 7.55025 0.000732437C7.53202 -0.000244146 7.51314 -0.000244146 7.49491 0.000732437ZM7.52258 1.22241L13.1476 4.66024V13.1257H11.0851V7.31323C11.087 7.16121 11.0275 7.01506 10.9197 6.90763C10.8123 6.80021 10.6658 6.74097 10.5138 6.74325C10.2036 6.74813 9.95618 7.00301 9.96008 7.31323V13.1257H8.08508V7.31323C8.08704 7.16121 8.02747 7.01506 7.91972 6.90763C7.8123 6.80021 7.66581 6.74097 7.51379 6.74325C7.20357 6.74813 6.95618 7.00301 6.96008 7.31323V13.1257H5.08508V7.31323C5.08704 7.16121 5.02747 7.01506 4.91972 6.90763C4.8123 6.80021 4.66581 6.74097 4.51379 6.74325C4.20357 6.74813 3.95618 7.00301 3.96008 7.31323V13.1257H1.89758V4.66024L7.52258 1.22241ZM7.52258 3.75073C7.10852 3.75073 6.77258 4.08667 6.77258 4.50073C6.77258 4.9148 7.10852 5.25073 7.52258 5.25073C7.93665 5.25073 8.27258 4.9148 8.27258 4.50073C8.27258 4.08667 7.93665 3.75073 7.52258 3.75073Z'
        fill='currentColor'
      />
    </g>
  ),
});

const SECTION_ICONS: Record<FooterSectionIcon, typeof HomeIcon> = {
  company: CompanyIcon,
  resources: SettingsIcon,
  'featured-programs': StarFullIcon,
  'only-at-udacity': CheckedFilledIcon,
  masters: GraduationIcon,
  schools: SchoolsIcon,
};

function SectionIcon({ icon }: { icon: FooterSectionIcon }) {
  const Icon = SECTION_ICONS[icon] ?? HomeIcon;

  return <Icon boxSize='20px' flexShrink={0} />;
}

export function Navigation({ navigation, newsletter }: { navigation: NavGroup[]; newsletter: ReactNode }) {
  const [userType, setUserType] = useState<'consumer' | 'enterprise'>('consumer');

  useEffect(() => {
    getUserType().then(setUserType);
  }, []);

  const filteredNavigation = useMemo(() => {
    return userType === 'enterprise' ? navigation.filter((navGroup) => navGroup.icon !== 'schools') : navigation;
  }, [navigation, userType]);

  const company = filteredNavigation.find((group) => group.icon === 'company');
  const resources = company?.nestedNavGroups?.[0];
  const featuredPrograms = filteredNavigation.find((group) => group.icon === 'featured-programs');
  const onlyAtUdacity = filteredNavigation.find((group) => group.icon === 'only-at-udacity');
  const masters = filteredNavigation.find((group) => group.icon === 'masters');
  const schools = filteredNavigation.find((group) => group.icon === 'schools');

  return (
    <>
      <Grid
        columnGap={{ md: '32px', lg: '64px' }}
        display={{ base: 'none', md: 'grid' }}
        gridTemplateColumns={{ md: 'minmax(0, 1fr) minmax(0, 1fr)', lg: '190px 190px minmax(0, 1fr)' }}
        gridTemplateRows={{ md: 'auto auto auto', lg: 'auto auto' }}
        rowGap={{ md: '24px', lg: '40px' }}
      >
        {company && (
          <DesktopNavigationGroup gridColumn={{ md: 1, lg: 1 }} gridRow={{ md: 1, lg: 1 }} navGroup={company} />
        )}
        {resources && (
          <DesktopNavigationGroup gridColumn={{ md: 2, lg: 1 }} gridRow={{ md: 1, lg: 2 }} navGroup={resources} />
        )}
        {featuredPrograms && (
          <DesktopNavigationGroup
            gridColumn={{ md: 1, lg: 2 }}
            gridRow={{ md: 2, lg: 1 }}
            navGroup={featuredPrograms}
          />
        )}
        {onlyAtUdacity && (
          <DesktopNavigationGroup gridColumn={{ md: 2, lg: 2 }} gridRow={{ md: 2, lg: 2 }} navGroup={onlyAtUdacity} />
        )}
        {schools && <DesktopSchools gridColumn={{ md: 1, lg: 3 }} gridRow={{ md: 3, lg: 1 }} navGroup={schools} />}
        <Box gridColumn={{ md: 2, lg: 3 }} gridRow={{ md: 3, lg: 2 }}>
          {masters && <DesktopMasters navGroup={masters} />}
          <Box mt={{ md: '24px', lg: '40px' }}>{newsletter}</Box>
        </Box>
      </Grid>
      <Box display={{ base: 'block', md: 'none' }}>
        <MobileNavigation navigation={filteredNavigation} />
        <Box mt='24px'>{newsletter}</Box>
      </Box>
    </>
  );
}

function DesktopSectionTitle({ children }: { children: ReactNode }) {
  return (
    <Text
      borderBottom='1px solid'
      borderBottomColor='whiteAlpha.400 !'
      color='white'
      fontFamily='condensed'
      fontSize='15px'
      fontWeight='500'
      letterSpacing='0.9px'
      lineHeight='1'
      pb='16px'
      textTransform='uppercase'
    >
      {children}
    </Text>
  );
}

function DesktopNavigationGroup({ navGroup, ...styleProps }: { navGroup: NavGroup } & StyleProps) {
  return (
    <Box {...styleProps}>
      <DesktopSectionTitle>{navGroup.title}</DesktopSectionTitle>
      <FooterNav gap='8px' groupTitle={navGroup.title} links={navGroup.links} mt='12px' />
    </Box>
  );
}

function DesktopSchools({ navGroup, ...styleProps }: { navGroup: NavGroup } & StyleProps) {
  return (
    <Box {...styleProps}>
      <DesktopSectionTitle>{navGroup.title}</DesktopSectionTitle>
      <Flex aria-label={`Navigation for ${navGroup.title} related links.`} as='nav' gap='64px' mt='12px'>
        {(navGroup.desktopColumns ?? []).map((links, index) => (
          <FooterNav key={index} asNav={false} gap='8px' groupTitle={navGroup.title} links={links} />
        ))}
      </Flex>
    </Box>
  );
}

function DesktopMasters({ navGroup, ...styleProps }: { navGroup: NavGroup } & StyleProps) {
  return (
    <Box {...styleProps}>
      <DesktopSectionTitle>{navGroup.title}</DesktopSectionTitle>
      <Flex gap='12px' mt='16px' wrap='wrap'>
        {navGroup.links.map((link) => (
          <LinkBox
            key={link.label}
            _hover={{ bgColor: 'whiteAlpha.100' }}
            alignItems='center'
            bgColor='whiteAlpha.50'
            border='1px solid'
            borderColor='whiteAlpha.50'
            borderRadius='4px'
            display='flex'
            flex='1'
            gap='12px'
            minW='280px'
            px='12px'
            py='10px'
            transition='background-color 120ms'
          >
            <Image alt='' height={24} src='/images/masters-seal-monochrome.svg' width={24} />
            <FooterLink groupTitle={navGroup.title} link={link} asOverlay noWrap />
          </LinkBox>
        ))}
      </Flex>
    </Box>
  );
}

export function MobileNavigation({ navigation }: { navigation: NavGroup[] }) {
  const flattenedNavigation = useMemo(() => flattenNavGroups(navigation), [navigation]);

  return (
    <Accordion
      bgColor='blackAlpha.300'
      border='1px solid'
      borderColor='whiteAlpha.400 !'
      borderRadius='4px'
      allowMultiple
    >
      {flattenedNavigation.map((navGroup, index) => (
        <AccordionItem
          key={navGroup.title}
          bgColor='blackAlpha.300'
          border={0}
          borderTop={index === 0 ? 0 : '0.5px solid'}
          borderTopColor='whiteAlpha.400 !'
        >
          <AccordionButton
            _hover={{ bgColor: 'blackAlpha.300', color: 'white' }}
            bgColor='blackAlpha.300'
            border='none !'
            borderBottomLeftRadius={index === flattenedNavigation.length - 1 ? '4px' : undefined}
            borderBottomRightRadius={index === flattenedNavigation.length - 1 ? '4px' : undefined}
            borderTopLeftRadius={index === 0 ? '4px' : undefined}
            borderTopRightRadius={index === 0 ? '4px' : undefined}
            color='grey.300'
            minH='52px'
            px='18px'
            py='14px'
            _expanded={
              index === flattenedNavigation.length - 1
                ? { bgColor: 'blackAlpha.300', borderBottomLeftRadius: 0, borderBottomRightRadius: 0, color: '#AFC6FF' }
                : { bgColor: 'blackAlpha.300', color: '#AFC6FF' }
            }
          >
            <Flex alignItems='center' flex='1' gap='10px' textAlign='left'>
              <SectionIcon icon={navGroup.icon} />
              <Text fontSize='16px' fontWeight='500'>
                {navGroup.title}
              </Text>
            </Flex>
            <AccordionIcon boxSize='16px' />
          </AccordionButton>
          <AccordionPanel
            bgColor='grey.700'
            border='none !'
            borderBottomLeftRadius={index === flattenedNavigation.length - 1 ? '4px' : undefined}
            borderBottomRightRadius={index === flattenedNavigation.length - 1 ? '4px' : undefined}
            p='0'
          >
            <FooterNav gap={0} groupTitle={navGroup.title} links={navGroup.links} mobile />
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function FooterNav({
  links,
  groupTitle,
  mobile = false,
  asNav = true,
  ...styleProps
}: { links: NavLink[]; groupTitle: string; mobile?: boolean; asNav?: boolean } & StyleProps) {
  // When asNav is false the caller supplies the surrounding <nav> landmark (e.g.
  // the schools section splits its links across columns but is one landmark),
  // so this stack must not introduce a second, duplicate nav landmark.
  const navProps = asNav ? { as: 'nav' as const, 'aria-label': `Navigation for ${groupTitle} related links.` } : {};

  return (
    <Stack {...navProps} {...styleProps}>
      {links.map((link, index) => {
        const icon = link.schoolIconPath && <Image alt='' height={20} src={link.schoolIconPath} width={18} />;

        // On mobile, make the whole row a hit target with LinkBox/LinkOverlay so a
        // tap anywhere on the row follows the link, not just the label text.
        if (mobile) {
          return (
            <LinkBox
              key={link.label}
              alignItems='center'
              borderBottom={index < links.length - 1 ? '0.5px solid' : undefined}
              borderColor='whiteAlpha.400 !'
              display='flex'
              gap='10px'
              minH='48px'
              px='18px'
              py='14px'
            >
              {icon}
              <FooterLink groupTitle={groupTitle} link={link} asOverlay mobile />
            </LinkBox>
          );
        }

        return (
          <Box key={link.label} alignItems='center' display='flex' gap='10px'>
            {icon}
            <FooterLink groupTitle={groupTitle} link={link} />
          </Box>
        );
      })}
    </Stack>
  );
}

export function FooterLink({
  link,
  groupTitle = 'Top level',
  mobile = false,
  noWrap = false,
  asOverlay = false,
  ...styleProps
}: { link: NavLink; groupTitle?: string; mobile?: boolean; noWrap?: boolean; asOverlay?: boolean } & StyleProps) {
  const baseUrl = useBaseUrl();
  const linkStyles = {
    _focusVisible: { outline: '2px solid', outlineColor: 'blue.200', outlineOffset: '2px' },
    _visited: { color: mobile ? 'white' : 'gray.200' },
    _hover: mobile ? { color: '#AFC6FF', textDecoration: 'none' } : { color: 'blue.200', textDecoration: 'underline' },
    color: mobile ? 'white' : 'gray.200',
    fontSize: mobile ? '14px' : '13px',
    fontWeight: '400',
    lineHeight: '1.4',
    textDecoration: 'none',
    whiteSpace: noWrap ? ('nowrap' as const) : undefined,
  };

  if (!link.href) {
    return (
      <Text {...linkStyles} {...styleProps}>
        {link.label}
      </Text>
    );
  }

  const href = link.isAbsolute ? link.href : `${baseUrl}${link.href}`;
  const handleClick = () => trackFooterLinkClick(link.label, groupTitle);

  // asOverlay expands the link's hit target to fill the nearest LinkBox ancestor,
  // making the entire mobile accordion row clickable.
  if (asOverlay) {
    return (
      <LinkOverlay
        {...linkStyles}
        as={NextLink}
        href={href}
        isExternal={link.isExternal}
        locale={false}
        onClick={handleClick}
        {...styleProps}
      >
        {link.label}
      </LinkOverlay>
    );
  }

  return (
    <Link {...linkStyles} href={href} isExternal={link.isExternal} locale={false} onClick={handleClick} {...styleProps}>
      {link.label}
    </Link>
  );
}
